import { normalizePhpListish } from '../../shared/php-listish';
import { readCreatureStatuses } from './creature-status-reader';
import { rederiveAuraOwnerSkillDelta, type AuraOwnerSkillDeltaInput, type AuraOwnerSkillLinesResult } from './aura-owner-skill-lines';
import { readLayerListEntryUuid, readInventoryField } from './inventory-raw-readers';
import { layerEntryTargetsSkill, skillModEntry } from './possessor-compile';
import contract from './c1-contract.json';
import { computedValue } from '../../shared/array-types';
import { fromBaseClone } from './from-base-context';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
type ProjectionInput = Pick<AuraOwnerSkillDeltaInput, 'previousHolder' | 'ownerId' | 'skills' | 'definition' | 'resolveOwner' | 'layerValue' | 'layerEntries'> & { holder: Row };

/** Status edits and owner flips use the same held-source checks and skill leaves. */
export function projectAuraOwnerSkills(input: ProjectionInput): AuraOwnerSkillLinesResult {
    const { holder, definition, layerValue, layerEntries } = input;
    const statuses = Object.values(readCreatureStatuses(holder, 'target', definition)).map(record).filter((row): row is Row => row !== null);
    const otherSources = [holder, ...statuses.filter(row => upper(readLayerListEntryUuid(row.status_caster_id)) !== upper(input.ownerId)
        || layerValue(record(row.status_arr), 'if_aura_owner_has_status_effect_active') == null)
        .map(row => record(row.status_arr)).filter((row): row is Row => row !== null)];
    for (const name of ['passive_ability', 'feat']) for (const entry of Object.values(holder[name] ?? {})) {
        if (record(entry)?.passive_conditions_active === false) continue;
        const def = record(definition(readLayerListEntryUuid(entry))); if (def) otherSources.push(def);
    }
    for (const item of normalizePhpListish(holder.inventory)) {
        if (!readInventoryField(record(item), 'equipped')) continue;
        const def = record(definition(String(readInventoryField(record(item), 'inventory_item_id') ?? ''))); if (def) otherSources.push(def);
    }
    return rederiveAuraOwnerSkillDelta({ ...input, skillEntries: contract.rollup.skillEntries,
        targets: layerEntryTargetsSkill, modEntry: skillModEntry,
        otherSourceTargets: (skill, kind) => otherSources.some(source => (kind === 'adv' ? ['skill_roll_adv', 'skill_roll_dis'] : ['skill_roll_mod'])
            .some(key => Object.values(layerEntries(source, key) as Row).some(entry => kind === 'adv' ? layerEntryTargetsSkill(entry, skill.item_id, skill.variable_name)
                : (() => { const mod = skillModEntry(entry); return mod !== null && mod.skill === upper(skill.item_id) && mod.mod !== 0; })()))) });
}

/** Bank only changed skill leaves; display projections and each memo own their lines. */
export function patchAuraOwnerSkillLeaves(member: Row, leaves: Row): Row {
    const computed = record(member.computed) ?? (member.computed = {}) as Row;
    const creature = record(computed.creature) ?? (computed.creature = {}) as Row;
    const skills = record(computedValue(member, 'creature', 'skills')) ?? (creature.skills = {}) as Row;
    for (const [key, value] of Object.entries(leaves)) {
        if (value === undefined) delete skills[key];
        else skills[key] = fromBaseClone(value); // dnr-clone: allow size=one changed aura skill leaf; why=each held memo owns its mutable skill lines
    }
    return member;
}
